import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';

interface ExperienceItem {
  title: string;
  company: string;
  period: string;
  location: string;
  description: string[];
}

const experiences: ExperienceItem[] = [
  {
    title: 'experience.bi.title',
    company: 'experience.bi.company',
    period: 'experience.bi.period',
    location: 'experience.bi.location',
    description: [
      'experience.bi.description.1',
      'experience.bi.description.2',
      'experience.bi.description.3',
      'experience.bi.description.4'
    ]
  },
  {
    title: 'experience.pickone.title',
    company: 'experience.pickone.company',
    period: 'experience.pickone.period',
    location: 'experience.pickone.location',
    description: [
      'experience.pickone.description.1',
      'experience.pickone.description.2',
      'experience.pickone.description.3',
      'experience.pickone.description.4',
      'experience.pickone.description.5'
    ]
  },
  {
    title: 'experience.freelance.title',
    company: 'experience.freelance.company',
    period: 'experience.freelance.period',
    location: 'experience.freelance.location',
    description: [
      'experience.freelance.description.1',
      'experience.freelance.description.2',
      'experience.freelance.description.3',
      'experience.freelance.description.4'
    ]
  }
];

const ExperienceCard: React.FC<{ experience: ExperienceItem; index: number }> = ({ experience, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { t } = useLanguage();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      className="card relative"
    >
      <div className="absolute -left-3 top-6 w-6 h-6 bg-secondary rounded-full" />
      <div className="absolute -left-1 top-7 w-2 h-2 bg-white rounded-full" />
      
      <div className="ml-4">
        <h3 className="text-xl font-bold text-secondary">{t(experience.title)}</h3>
        <div className="flex flex-wrap gap-2 text-sm text-base-content/60 mb-4">
          <span>{t(experience.company)}</span>
          <span>•</span>
          <span>{t(experience.period)}</span>
          <span>•</span>
          <span>{t(experience.location)}</span>
        </div>
        <ul className="list-disc list-inside space-y-2 text-base-content/80">
          {experience.description.map((item, i) => (
            <li key={i}>{t(item)}</li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const Experience: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <section id="experience" className="section-padding">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="heading-secondary mb-4 text-primary">{t('experience.title')}</h2>
          <div className="w-24 h-1 bg-secondary mx-auto rounded-full" />
        </motion.div>

        <div className="space-y-8">
          {experiences.map((experience, index) => (
            <ExperienceCard key={index} experience={experience} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience; 